'use client';

import { useState } from 'react';
import { Building2, MapPin } from 'lucide-react';
import BusinessInternetCheck from './BusinessInternetCheck';
import MultiSiteCheck from './MultiSiteCheck';

/**
 * Asks the one qualifying question that matters before anything else.
 *
 * Commission is a percentage of MRC, so a twelve-site operator is worth roughly
 * twelve times a single-office lead — but nobody searches for "multi location
 * business internet" (no volume data at all), so multi-site can only ever be
 * caught inside a funnel fed by single-site queries. Putting the choice first
 * is the cheapest possible qualification: the high-value visitor self-selects
 * before we have asked for so much as an email.
 */
export default function CheckModeSwitch() {
  const [mode, setMode] = useState<'single' | 'multi'>('single');

  const base =
    'flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm transition-colors border';
  const active = 'bg-[#2813e8] border-[#2813e8] text-white';
  const idle =
    'bg-white dark:bg-gray-800/50 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-[#2813e8] hover:text-[#2813e8] dark:hover:text-white';

  return (
    <div className="space-y-8">
      <div>
        <h2 className="sr-only">How many locations are you checking?</h2>
        <div
          role="tablist"
          aria-label="How many locations are you checking?"
          className="flex flex-col sm:flex-row gap-3"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'single'}
            onClick={() => setMode('single')}
            className={`${base} ${mode === 'single' ? active : idle}`}
          >
            <MapPin className="w-4 h-4" />
            One location
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'multi'}
            onClick={() => setMode('multi')}
            className={`${base} ${mode === 'multi' ? active : idle}`}
          >
            <Building2 className="w-4 h-4" />
            Multiple locations
          </button>
        </div>
        {mode === 'single' && (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 text-center">
            More than one site?{' '}
            <button
              type="button"
              onClick={() => setMode('multi')}
              className="text-[#2813e8] dark:text-blue-400 underline underline-offset-2 font-medium"
            >
              Check them all at once
            </button>{' '}
            — it takes no longer than checking one.
          </p>
        )}
      </div>

      {mode === 'single' ? <BusinessInternetCheck /> : <MultiSiteCheck />}
    </div>
  );
}
